import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  AdminService,
  AuthService,
  MetadataService,
  type AuthConfigOut,
  type FeatureFlagAdminOut,
  type MetadataOut,
} from '@/api'
import { extractErrorMessage } from '@/utils/apiError'
import { isDevBuild } from '@/utils/isDev'

type PreviewResolver = (key: string, serverEnabled: boolean) => boolean | undefined
let previewResolver: PreviewResolver | null = null

export function setFeaturePreviewResolver(resolver: PreviewResolver | null): void {
  previewResolver = resolver
}

function legacyMetadata(config: AuthConfigOut): MetadataOut {
  return {
    environment: config.environment ?? 'local',
    auth: {
      google_enabled: config.google_enabled,
      email_password_enabled: config.email_password_enabled,
    },
    capabilities: {
      ai_features_enabled: config.ai_features_enabled ?? false,
      api_key_auth_enabled: config.api_key_auth_enabled ?? true,
      api_key_access: config.api_key_access ?? 'read_only',
    },
    feature_flags: [],
  }
}

async function fetchMetadataWithLegacyFallback(allowLegacyFallback = true): Promise<MetadataOut> {
  try {
    const response = await MetadataService.metadataMetadataGet()
    if (!response.auth || !response.capabilities || !Array.isArray(response.feature_flags)) {
      throw new Error('The deployment metadata response is incompatible with this frontend.')
    }
    return response
  } catch (metadataError) {
    if (!allowLegacyFallback) throw metadataError
    try {
      return legacyMetadata(await AuthService.authConfigAuthConfigGet())
    } catch {
      throw metadataError
    }
  }
}

export const useMetadataStore = defineStore('metadata', () => {
  const metadata = ref<MetadataOut | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchedAt = ref<number | null>(null)
  const adminFlags = ref<FeatureFlagAdminOut[]>([])
  const adminLoading = ref(false)
  const adminError = ref<string | null>(null)
  const adminStale = ref(false)
  const adminLoadedAt = ref<number | null>(null)
  const availabilityWarning = ref<string | null>(null)

  let nextGeneration = 0
  let latestAppliedGeneration = 0
  let mutationBarrier = 0
  let inFlight: Promise<MetadataOut | null> | null = null

  const featureFlags = computed(() => metadata.value?.feature_flags ?? [])
  const environment = computed(() => metadata.value?.environment ?? 'local')
  const auth = computed(() => metadata.value?.auth ?? null)

  function isFeatureEnabledOnServer(key: string): boolean {
    return featureFlags.value.find((flag) => flag.key === key)?.enabled ?? false
  }

  function isFeatureVisible(key: string): boolean {
    const serverEnabled = isFeatureEnabledOnServer(key)
    if (!isDevBuild || previewResolver === null) return serverEnabled
    return previewResolver(key, serverEnabled) ?? serverEnabled
  }

  function isFeaturePreviewed(key: string): boolean {
    return isFeatureVisible(key) !== isFeatureEnabledOnServer(key)
  }

  function refresh(
    options: { force?: boolean; userInitiated?: boolean; legacyFallback?: boolean } = {},
  ) {
    if (inFlight && !options.force) return inFlight
    const generation = ++nextGeneration
    loading.value = metadata.value === null
    if (options.userInitiated) error.value = null

    const request = fetchMetadataWithLegacyFallback(options.legacyFallback ?? true)
      .then((response) => {
        if (generation < mutationBarrier || generation < latestAppliedGeneration) {
          return metadata.value
        }
        metadata.value = response
        latestAppliedGeneration = generation
        lastFetchedAt.value = Date.now()
        error.value = null
        return response
      })
      .catch((caught: unknown) => {
        if (generation >= mutationBarrier && generation >= latestAppliedGeneration) {
          error.value = extractErrorMessage(caught)
        }
        return null
      })
      .finally(() => {
        if (inFlight === request) {
          inFlight = null
          loading.value = false
        }
      })
    inFlight = request
    return request
  }

  async function refreshIfStale(maxAgeMs = 30_000): Promise<MetadataOut | null> {
    if (lastFetchedAt.value !== null && Date.now() - lastFetchedAt.value < maxAgeMs) {
      return metadata.value
    }
    return refresh()
  }

  async function refreshAdminFlags(): Promise<FeatureFlagAdminOut[] | null> {
    adminLoading.value = true
    adminError.value = null
    try {
      const response = await AdminService.listFeatureFlagsAdminFeatureFlagsGet()
      adminFlags.value = [...response].sort((left, right) => left.key.localeCompare(right.key))
      adminLoadedAt.value = Date.now()
      adminStale.value = false
      return adminFlags.value
    } catch (caught) {
      adminError.value = extractErrorMessage(caught)
      adminStale.value = adminLoadedAt.value !== null
      return null
    } finally {
      adminLoading.value = false
    }
  }

  async function updateFeatureFlag(
    key: string,
    enabled: boolean,
    expectedRevision: number,
    confirmEnvironment?: string,
  ): Promise<FeatureFlagAdminOut> {
    const updated = await AdminService.updateFeatureFlagAdminFeatureFlagsKeyPut({
      key,
      requestBody: {
        enabled,
        expected_revision: expectedRevision,
        confirm_environment: confirmEnvironment,
      },
    })
    adminFlags.value = adminFlags.value
      .map((flag) => (flag.key === key ? updated : flag))
      .sort((left, right) => left.key.localeCompare(right.key))
    adminStale.value = false
    adminLoadedAt.value = Date.now()

    // Mutation barrier: an older GET may still be in flight. Apply the PUT
    // result now, advance beyond every older generation, then force a new GET.
    mutationBarrier = ++nextGeneration
    latestAppliedGeneration = mutationBarrier
    if (metadata.value) {
      const existing = metadata.value.feature_flags ?? []
      const publicFlag = {
        key: updated.key,
        label: updated.label,
        description: updated.description,
        enabled: updated.enabled,
      }
      metadata.value = {
        ...metadata.value,
        feature_flags: existing.some((flag) => flag.key === key)
          ? existing.map((flag) => (flag.key === key ? publicFlag : flag))
          : [...existing, publicFlag].sort((left, right) => left.key.localeCompare(right.key)),
      }
    }
    await refreshAvailability()
    return updated
  }

  async function refreshAvailability(): Promise<boolean> {
    availabilityWarning.value = null
    if ((await refresh({ force: true, userInitiated: true, legacyFallback: false })) === null) {
      availabilityWarning.value = 'Application availability refresh failed.'
      return false
    }
    return true
  }

  function $reset(): void {
    metadata.value = null
    loading.value = false
    error.value = null
    lastFetchedAt.value = null
    adminFlags.value = []
    adminLoading.value = false
    adminError.value = null
    adminStale.value = false
    adminLoadedAt.value = null
    availabilityWarning.value = null
    nextGeneration = 0
    latestAppliedGeneration = 0
    mutationBarrier = 0
    inFlight = null
  }

  return {
    metadata,
    loading,
    error,
    lastFetchedAt,
    featureFlags,
    environment,
    auth,
    adminFlags,
    adminLoading,
    adminError,
    adminStale,
    adminLoadedAt,
    availabilityWarning,
    isFeatureEnabledOnServer,
    isFeatureVisible,
    isFeaturePreviewed,
    refresh,
    refreshIfStale,
    refreshAdminFlags,
    updateFeatureFlag,
    refreshAvailability,
    $reset,
  }
})
